import styled, { createGlobalStyle, css } from 'styled-components';
import {
  Box,
  isDarkThemeMixin,
  isMobileStyleMixin,
  isNotDarkThemeMixin,
  Link,
  styleMixinFocusOutline,
  styleMixinInvertedFocusOutline,
} from '@amzn/storm-ui';
import { ChromeThemeStyles } from 'src/commons/components/ChromeApp/ChromeAppUtils';
import { MOBILE_MENU_Z_INDEX } from 'src/commons/constants';

const marginStartMixin = css<{ shouldShiftAppWrapper: boolean }>`
  ${(props) =>
    props.shouldShiftAppWrapper ? 'margin-inline-start: 64px;' : ''}
`;
export const GlobalStyle = createGlobalStyle<{
  shouldShiftAppWrapper: boolean;
  isFromRodeo?: boolean;
  theme: ChromeThemeStyles;
  isInterstitialPage?: boolean;
  isMobile?: boolean;
}>`
  html {
    height: 100vh;
    display: flex;
    flex-direction: column;
  }

  body {
    flex: 1;
    display: flex;
    flex-direction: column;
    font-family: "Amazon Ember", Arial, Helvetica, sans-serif;
    margin: 0;
    padding: 0;
    ${({ theme, isInterstitialPage = false, isMobile = false }) =>
      isInterstitialPage &&
      css`
        background-color: ${isMobile
          ? theme.color.gray[75]
          : theme.color.gray[50]};
      `}
  }

  .chrome-wrapper {
    display: flex;
    flex-direction: column;
  }

  .app-and-pivot-wrapper {
    flex: 1;
    display: flex;
    flex-direction: ${({ shouldShiftAppWrapper }) =>
      shouldShiftAppWrapper ? 'row' : 'column'};
    ${marginStartMixin}
    transition: margin-inline-start 0.15s ease;
  }

  #PivotContainer {
    width: auto;
    background-color: transparent;
    ${({ shouldShiftAppWrapper }) =>
      shouldShiftAppWrapper
        ? css`
            height: 100%;
          `
        : css``};
  }

  #ChromeHeaderContainer {
    position: relative;
  }

  #MainMenuContainer {
    min-height: auto;
  }

  .app-body-wrapper {
    flex: 1;
    overflow: auto;
    ${({ isFromRodeo }) => (isFromRodeo ? 'padding: 14px 18px 18px;' : '')}
  }

  #app-content {
    flex: 1;
    // min-width: 0 lets wide app content (UCM carousel) shrink inside the flex
    // row without a scroll container. An overflow value other than visible
    // would make this the sticky root for app content, and sticky descendants
    // would pin to this element instead of the viewport.
    min-width: 0;
    ${({ isFromRodeo }) => (isFromRodeo ? 'padding: 14px 18px 18px;' : '')}
  }

  #a-page {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  #sc-content-container {
    flex: 1;
  }

  .pivot-and-app-wrapper {
    flex: 1;
  }

  .sc-content-inner-container {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
`;

export const MenuShiftWrapper = styled.div<{
  shouldShiftAppWrapper: boolean;
}>`
  ${marginStartMixin}
`;

/**
 * Host element that global-header popovers (account / user dropdown, etc.) portal
 * into. `position: relative` is load-bearing: it lets the z-index that
 * `GlobalHeader` assigns at runtime (nearest-ancestor z-index + inline-portal
 * increment, mirroring StormUI's `InlinePortalProvider`) actually take effect as a
 * stacking context, so the portalled popover surfaces (StormUI `layer3` / 500) paint
 * ABOVE a StormUI `FocusOverlay` (also `layer3` / 500). It sits outside
 * `HeaderContainer` so it is not capped by the fixed header's own `z-index: 399`
 * stacking context.
 */
export const HeaderPortal = styled.div`
  position: relative;
`;

export const HeaderContainer = styled.div`
  position: fixed;
  width: 100%;
  z-index: 399; // header should have most precedence
  ${isNotDarkThemeMixin(css`
    box-shadow: ${({ theme }) => theme.elevation.shadow.card};
  `)};
`;

export const MarketplaceSwitcherWrapper = styled(Box).attrs({
  padding: 'none',
})<{ isFromTrim?: boolean }>`
  border-radius: 0;

  :not(:empty) {
    padding: ${({ theme }) =>
      `${theme.spacing.base.default} ${theme.spacing.base.default} ${theme.spacing.xxsmall.default}`};

    ${isMobileStyleMixin(css`
      padding: ${({ theme }) =>
        `${theme.spacing.small.mobile} ${theme.spacing.small.mobile} ${theme.spacing.xxsmall.mobile} `};
    `)};

    ${isDarkThemeMixin(css`
      background-color: ${({ theme }) => theme.surface.offset};
    `)};

    // Inside the non-empty guard with the padding, not outside it: a wrapper
    // whose switcher resolved to no data must paint nothing, and a shadow on a
    // zero-height box still draws a visible line across the page.
    ${({ theme, isFromTrim }) =>
      isFromTrim &&
      css`
        box-shadow: ${theme.elevation.shadow.card};
      `};
  }
`;

/**
 * The e2e id lives on `attrs` rather than on each render site so that a new
 * render site cannot omit it. It is the only stable selector for the region
 * switcher: the control itself comes from
 * `@amzn/d16-g-adsp-region-switcher-ui`, so without this id there is nothing in
 * the DOM that identifies which of the two mutually exclusive switchers
 * rendered.
 *
 * This is a sibling of `MarketplaceSwitcherWrapper`, never nested inside it.
 * Only one of the two switchers ever renders (see `resolveSwitcherVisibility`),
 * and nesting made the outer wrapper permanently non-`:empty`, which defeated
 * the padding guard and produced an empty padded band on data-less pages.
 */
export const RegionSwitcherWrapper = styled(MarketplaceSwitcherWrapper).attrs({
  'data-ccx-e2e-id': 'aac-region-switcher-wrapper',
})``;

export const LogoWrapper = styled(Link)<{ $wide?: boolean }>`
  width: ${({ $wide }) => ($wide ? '180px' : '120px')};
  height: 30px;
  margin: ${({ theme }) => theme.spacing.xxxsmall.default}
    ${({ theme }) => theme.spacing.small.default} 0
    ${({ theme }) => theme.spacing.large.default};
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.border.radius.medium};

  img {
    width: 100%;
    height: 100%;
  }

  :focus {
    outline: none;
  }

  :focus-visible {
    ${styleMixinInvertedFocusOutline};

    ${isDarkThemeMixin(css`
      ${styleMixinFocusOutline};
    `)};
  }
`;

export const MobileHeaderContainer = styled.div`
  display: block;
  position: fixed;
  top: 0;
  width: 100%;
  background-color: ${({ theme }) => theme.header.contextBar.backgroundColor};
  z-index: ${MOBILE_MENU_Z_INDEX};
`;

export const StickyMobileHeader = styled.div`
  position: fixed;
  width: 100%;
  z-index: ${MOBILE_MENU_Z_INDEX};
`;

export const A3ChatContainer = styled.div`
  position: relative;
  z-index: 399;
`;

export const EmptyContextBar = styled.div`
  flex-grow: 1;
`;
